import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ConfirmDialog from "@/components/ConfirmDialog";
import { F } from "@/pages/Employees";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { nama: "", kategori: "tunjangan", metode: "nominal_tetap", nominal_default: 0, aktif: true, catatan: "" };
const METODE = { per_hari: "Per Hari", per_periode: "Per Periode", nominal_tetap: "Nominal Tetap", manual: "Manual" };

export default function AllowanceTypes() {
  const { user } = useAuth();
  const canWrite = ["super_admin", "hr", "direktur"].includes(user.role);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [del, setDel] = useState(null);

  const load = () => api.get("/allowance-types").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nama) return toast.error("Nama wajib diisi");
    const body = { ...form, nominal_default: Number(form.nominal_default || 0) };
    try {
      if (editing) await api.put(`/allowance-types/${editing}`, body);
      else await api.post("/allowance-types", body);
      toast.success("Tersimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/allowance-types/${del.id}`); toast.success("Dihapus"); setDel(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const columns = [
    { key: "nama", label: "Nama", sortable: true, render: (r) => <span className="font-semibold">{r.nama}</span> },
    { key: "kategori", label: "Kategori", sortable: true, render: (r) => <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.kategori === "potongan" ? "bg-rose-50 text-rose-700" : "bg-blue-50 text-blue-700"}`}>{r.kategori === "potongan" ? "Potongan" : "Tunjangan"}</span> },
    { key: "metode", label: "Metode", render: (r) => METODE[r.metode] || r.metode },
    { key: "nominal_default", label: "Nominal Default", align: "right", render: (r) => rupiah(r.nominal_default) },
    {
      key: "act", label: "Aksi", render: (r) => canWrite && (
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" data-testid={`at-edit-${r.id}`} onClick={() => { setForm({ ...empty, ...r }); setEditing(r.id); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="text-red-600" data-testid={`at-delete-${r.id}`} onClick={() => setDel(r)}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Jenis Tunjangan & Potongan" subtitle="Kelola jenis komponen tanpa mengubah kode">
        {canWrite && <Button className="bg-blue-800 hover:bg-blue-900" data-testid="at-add-btn" onClick={() => { setForm(empty); setEditing(null); setOpen(true); }}><Plus className="h-4 w-4 mr-1" /> Tambah Jenis</Button>}
      </PageHeader>
      <DataTable columns={columns} data={rows} searchKeys={["nama"]} testid="at-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Tambah"} Jenis</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <F label="Nama *"><Input data-testid="at-f-nama" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} /></F>
            <F label="Kategori">
              <Select value={form.kategori} onValueChange={(v) => setForm({ ...form, kategori: v })}>
                <SelectTrigger data-testid="at-f-kategori"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="tunjangan">Tunjangan</SelectItem><SelectItem value="potongan">Potongan</SelectItem></SelectContent>
              </Select>
            </F>
            <F label="Metode">
              <Select value={form.metode} onValueChange={(v) => setForm({ ...form, metode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(METODE).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Nominal Default"><Input type="number" value={form.nominal_default} onChange={(e) => setForm({ ...form, nominal_default: e.target.value })} /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="at-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} destructive testid="at-delete-dialog"
        title="Hapus Jenis?" description={`Yakin menghapus ${del?.nama}?`} confirmText="Hapus" onConfirm={doDelete} />
    </div>
  );
}
